import { useState, useEffect } from 'react';
import {
    styled,
    Box,
    CircularProgress,
    Typography,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';

import { TextArea } from 'components/ui/inputs';
import { Page } from 'components/ui/layout';
import FormSubmitBar from 'components/forms/FormSubmitBar';
import { Section, Header, Content } from 'components/ui/forms';

import LenientExample from 'images/orphan-validator-lenient.png'
import StrictExample from 'images/orphan-validator-strict.png'

import { useAuth } from 'context/auth';
import { useNotification } from 'context/notification';
import { useTextInput } from 'hooks/forms';
import fetchExpresso from 'utility/fetchExpresso';
import { validateURL } from 'utility/validation';
import { Alert } from '@mui/material';
import { useEvent } from 'context/event';

function formatRobinLabel(webhook) {
    return webhook.status === 1
        ? webhook.webhook_label
        : `${webhook.webhook_label} (disabled)`;
}

export default function Checkout(props) {
    const eventId = props.match.params.id;

    const event = useEvent();
    const { auth } = useAuth();

    const [status, setStatus] = useState('loading');
    const [updating, setUpdating] = useState(false);

    const [defaultValues, setDefaultValues] = useState(null);
    const [robinAgents, setRobinAgents] = useState([]);

    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const { notify } = useNotification();

    useEffect(() => {
        Promise.all([
            fetchExpresso(`/apiv2/events/${eventId}/checkout_customization`),
            fetchExpresso(`/apiv2/hosts/${auth.hostId}/webhooks`),
        ])
            .then(async ([checkoutRes, webhooksRes]) => {
                if (checkoutRes.status !== 200) {
                    throw new Error();
                }

                const data = await checkoutRes.json();
                // Robin webhooks are optional context; if the call fails we just
                // hide the dropdown options rather than failing the whole page.
                const webhooks =
                    webhooksRes.status === 200 ? await webhooksRes.json() : [];

                setRobinAgents(webhooks.filter((w) => w.webhook_id === 1));

                setStatus('success');
                setDefaultValues({
                    fpEnabled: Boolean(data.fine_print),
                    finePrint: data.fine_print || '',
                    ctaEnabled: Boolean(data.cta_btn_url),
                    ctaDesc: data.cta_desc || '',
                    ctaBtnText: data.cta_btn_text || '',
                    ctaBtnUrl: data.cta_btn_url || '',
                    hostNoteEnabled: Boolean(data.host_note),
                    hostNote: data.host_note || '',
                    metaPixelEnabled: Boolean(data.meta_pixel_id),
                    metaPixel: data.meta_pixel_id || '',
                    googleAnalyticsEnabled: Boolean(data.google_tracking_id),
                    googleAnalytics: data.google_tracking_id || '',
                    robinEnabledFlag: Boolean(data.robin_enabled_flag),
                    robinEnabled: Boolean(data.host_webhook_id),
                    hostWebhookId: data.host_webhook_id || '',
                    orphanSeats: data.orphan_seats || '',
                });
                setUnsavedChanges(false);
            })
            .catch(() => setStatus('error'));
    }, [eventId, auth.hostId]);

    const handleEdit = (form) => {
        if (updating) return;
        setUpdating(true);
        fetchExpresso(`/apiv2/events/${eventId}/checkout_customization`, {
            method: 'PUT',
            body: {
                finePrint: form.finePrint,
                ctaDescription: form.ctaDesc,
                ctaButtonText: form.ctaBtnText,
                ctaButtonURL: form.ctaBtnUrl,
                hostNote: form.hostNote,
                metaPixel: form.metaPixel,
                googleAnalytics: form.googleAnalytics,
                hostWebhookId: form.hostWebhookId,
                orphanSeats: form.orphanSeats,
            },
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Event Updated');
                    setDefaultValues((dv) => ({
                        ...dv, // Will preserve flags like robinEnabledFlag which are not included in form payload
                        fpEnabled: Boolean(form.finePrint),
                        finePrint: form.finePrint || '',
                        ctaEnabled: Boolean(form.ctaBtnUrl),
                        ctaDesc: form.ctaDesc || '',
                        ctaBtnText: form.ctaBtnText || '',
                        ctaBtnUrl: form.ctaBtnUrl || '',
                        hostNoteEnabled: Boolean(form.hostNote),
                        hostNote: form.hostNote || '',
                        metaPixelEnabled: Boolean(form.metaPixel),
                        metaPixel: form.metaPixel || '',
                        googleAnalyticsEnabled: Boolean(form.googleAnalytics),
                        googleAnalytics: form.googleAnalytics || '',
                        robinEnabled: Boolean(form.hostWebhookId),
                        hostWebhookId: form.hostWebhookId || '',
                        orphanSeats: form.orphanSeats || '',
                    }));
                    setUnsavedChanges(false);
                } else {
                    notify.error('There was a problem updating your event');
                }
            })
            .catch(() => notify.error('There was a problem updating your event'))
            .finally(() => setUpdating(false));
    };

    // Waiting for default values to load
    if (status === 'loading' || defaultValues === null) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <Typography variant="h5">There was a problem finding your event</Typography>
                    <Box height={32} />
                    <Button variant="outlined" onClick={() => props.history.push('/events')}>
                        Back to Events
                    </Button>
                </Box>
            </Page>
        );
    }

    return (
        <Page width="md">
            <CheckoutForm
                seatsEnabled={event.seats_flag === 1}
                robinAgents={robinAgents}
                defaultValues={defaultValues}
                unsavedChanges={unsavedChanges}
                updating={updating}
                onUnsavedStateChange={(value) => setUnsavedChanges(value)}
                onSubmit={handleEdit}
            />
        </Page>
    );
}

function CheckoutForm(props) {
    const { seatsEnabled, robinAgents, defaultValues, unsavedChanges, updating, onUnsavedStateChange, onSubmit } = props;

    const [fpEnabled, setFpEnabled] = useState(defaultValues.fpEnabled);
    const [ctaEnabled, setCtaEnabled] = useState(defaultValues.ctaEnabled);
    const [hostNoteEnabled, setHostNoteEnabled] = useState(defaultValues.hostNoteEnabled);
    const [metaPixelEnabled, setMetaPixelEnabled] = useState(defaultValues.metaPixelEnabled);
    const [googleAnalyticsEnabled, setGoogleAnalyticsEnabled] = useState(
        defaultValues.googleAnalyticsEnabled,
    );
    const [robinEnabled, setRobinEnabled] = useState(defaultValues.robinEnabled);
    const [hostWebhookId, setHostWebhookId] = useState(defaultValues.hostWebhookId || '');
    const [robinError, setRobinError] = useState('');
    const selectedRobinAgent = robinAgents.find(
        (agent) => String(agent.host_webhook_id) === String(hostWebhookId),
    );

    const finePrint = useTextInput({
        defaultValue: defaultValues.finePrint,
        disabled: !defaultValues.fpEnabled,
        maxLength: 2000
    });
    const ctaDesc = useTextInput({
        defaultValue: defaultValues.ctaDesc,
        disabled: !defaultValues.ctaEnabled,
        maxLength: 500,
    });
    const ctaBtnText = useTextInput({
        defaultValue: defaultValues.ctaBtnText,
        disabled: !defaultValues.ctaEnabled,
        maxLength: 25,
    });
    const ctaBtnUrl = useTextInput({
        defaultValue: defaultValues.ctaBtnUrl,
        disabled: !defaultValues.ctaEnabled,
        maxLength: 200,
    });
    const hostNote = useTextInput({
        defaultValue: defaultValues.hostNote,
        disabled: !defaultValues.hostNoteEnabled,
        maxLength: 2000
    });
    const metaPixel = useTextInput({
        defaultValue: defaultValues.metaPixel,
        disabled: !defaultValues.metaPixelEnabled,
        format: 'numeric',
    });

    const googleAnalytics = useTextInput({
        defaultValue: defaultValues.googleAnalytics,
        disabled: !defaultValues.googleAnalyticsEnabled,
    });
    const orphanSeats = useTextInput({
        defaultValue: defaultValues.orphanSeats,
    });

    const orphanSeatOptions = [
        {
            value: 'allow',
            label: 'Always Allow',
            description: 'Customers can leave single-seat gaps',
        },
        {
            value: 'lenient',
            label: 'Avoid if Possible',
            description:
                "Only allowed if there's no way for customer to book their seats nearby",
            image: LenientExample,
            imageAlt: 'avoid if possible example',
        },
        {
            value: 'block',
            label: 'Strict',
            description:
                "Only allowed if there's no way for customer to book their seats anywhere in the seat category",
            image: StrictExample,
            imageAlt: 'strict example',
        },
    ];

    const handleFpToggle = (e) => {
        setFpEnabled(e.target.checked);
        finePrint.setDisabled(!e.target.checked);
    };

    const handleCtaToggle = (e) => {
        setCtaEnabled(e.target.checked);
        ctaDesc.setDisabled(!e.target.checked);
        ctaBtnText.setDisabled(!e.target.checked);
        ctaBtnUrl.setDisabled(!e.target.checked);
    };

    const handleHostNoteToggle = (e) => {
        setHostNoteEnabled(e.target.checked);
        hostNote.setDisabled(!e.target.checked);
    };

    const handleMetaPixelToggle = (e) => {
        setMetaPixelEnabled(e.target.checked);
        metaPixel.setDisabled(!e.target.checked);
    };

    const handleGoogleAnalyticsToggle = (e) => {
        setGoogleAnalyticsEnabled(e.target.checked);
        googleAnalytics.setDisabled(!e.target.checked);
    };

    const handleRobinToggle = (e) => {
        setRobinEnabled(e.target.checked);
        setRobinError('');
        if (!e.target.checked) {
            setHostWebhookId('');
        }
    };

    const handleRobinChange = (e) => {
        setHostWebhookId(e.target.value);
        setRobinError('');
    };

    // Compare current/default form values; The save + reset buttons are activated when unsaved changes exist
    useEffect(() => {
        if (
            fpEnabled === defaultValues.fpEnabled &&
            ctaEnabled === defaultValues.ctaEnabled &&
            hostNoteEnabled === defaultValues.hostNoteEnabled &&
            finePrint.value === defaultValues.finePrint &&
            ctaDesc.value === defaultValues.ctaDesc &&
            ctaBtnText.value === defaultValues.ctaBtnText &&
            ctaBtnUrl.value === defaultValues.ctaBtnUrl &&
            hostNote.value === defaultValues.hostNote &&
            metaPixel.value === defaultValues.metaPixel &&
            googleAnalytics.value === defaultValues.googleAnalytics &&
            robinEnabled === defaultValues.robinEnabled &&
            String(hostWebhookId || '') === String(defaultValues.hostWebhookId || '') &&
            orphanSeats.value === defaultValues.orphanSeats
        ) {
            // Form values have not changed
            if (unsavedChanges === true) {
                onUnsavedStateChange(false);
            }
        } else {
            // Form values have changed
            if (unsavedChanges === false) {
                onUnsavedStateChange(true);
            }
        }
    });

    const handleReset = () => {
        setFpEnabled(defaultValues.fpEnabled);
        setCtaEnabled(defaultValues.ctaEnabled);
        setHostNoteEnabled(defaultValues.hostNoteEnabled);
        setMetaPixelEnabled(defaultValues.metaPixelEnabled);
        setGoogleAnalyticsEnabled(defaultValues.googleAnalyticsEnabled);
        setRobinEnabled(defaultValues.robinEnabled);
        setHostWebhookId(defaultValues.hostWebhookId || '');
        setRobinError('');

        finePrint.setDisabled(!defaultValues.fpEnabled);
        hostNote.setDisabled(!defaultValues.hostNoteEnabled);
        ctaDesc.setDisabled(!defaultValues.ctaEnabled);
        ctaBtnText.setDisabled(!defaultValues.ctaEnabled);
        ctaBtnUrl.setDisabled(!defaultValues.ctaEnabled);
        metaPixel.setDisabled(!defaultValues.metaPixelEnabled);
        googleAnalytics.setDisabled(!defaultValues.googleAnalyticsEnabled);

        finePrint.setValue(defaultValues.finePrint);
        ctaDesc.setValue(defaultValues.ctaDesc);
        ctaBtnText.setValue(defaultValues.ctaBtnText);
        ctaBtnUrl.setValue(defaultValues.ctaBtnUrl);
        hostNote.setValue(defaultValues.hostNote);
        metaPixel.setValue(defaultValues.metaPixel);
        googleAnalytics.setValue(defaultValues.googleAnalytics);
        orphanSeats.setValue(defaultValues.orphanSeats);
    };

    const handleSubmit = () => {
        let formError = false;

        if (seatsEnabled) {
            if (orphanSeats.value.trim() === '') {
                orphanSeats.setError('You must select one option');
                formError = true;
            }
        }

        if (fpEnabled) {
            if (finePrint.value.trim() === '') {
                finePrint.setError('This field cannot be blank');
                formError = true;
            }
        }

        if (ctaEnabled) {
            if (ctaDesc.value.trim() === '') {
                ctaDesc.setError('This field cannot be blank');
                formError = true;
            }
            if (ctaBtnText.value.trim() === '') {
                ctaBtnText.setError('This field cannot be blank');
                formError = true;
            }

            if (ctaBtnUrl.value.trim() === '') {
                ctaBtnUrl.setError('This field cannot be blank');
                formError = true;
            } else if (validateURL(ctaBtnUrl.value.trim()) === false) {
                ctaBtnUrl.setError(
                    'Make sure your link is properly formatted. Example: https://example.com',
                );
                formError = true;
            }
        }

        if (hostNoteEnabled) {
            if (hostNote.value.trim() === '') {
                hostNote.setError('This field cannot be blank');
                formError = true;
            }
        }

        if (metaPixelEnabled) {
            if (metaPixel.value === '') {
                metaPixel.setError('This field cannot be blank');
                formError = true;
            } else if (metaPixel.value.length < 15) {
                metaPixel.setError('Meta Pixels should be between 15 and 16 characters');
                formError = true;
            }
        }

        if (googleAnalyticsEnabled) {
            if (googleAnalytics.value === '') {
                googleAnalytics.setError('This field cannot be blank');
                formError = true;
            } else if (googleAnalytics.value.length < 12) {
                googleAnalytics.setError('Google Analytics should be 12 characters');
                formError = true;
            }
        }

        if (robinEnabled && !hostWebhookId) {
            setRobinError('Select a Robin agent');
            formError = true;
        }

        if (formError) return;

        onSubmit({
            finePrint: finePrint.value.trim() || null,
            ctaDesc: ctaDesc.value.trim() || null,
            ctaBtnText: ctaBtnText.value.trim() || null,
            ctaBtnUrl: ctaBtnUrl.value.trim() || null,
            hostNote: hostNote.value.trim() || null,
            metaPixel: metaPixel.value.trim() || null,
            googleAnalytics: googleAnalytics.value.trim() || null,
            hostWebhookId: robinEnabled && hostWebhookId ? Number(hostWebhookId) : null,
            orphanSeats: seatsEnabled ? orphanSeats.value : null,
        });
    };

    return (
        <FormContainer>
            <Box mb={4}>
                <Alert severity="info">
                    Personalize and fine-tune your customer's checkout experience. These settings
                    are optional and can be tailored to fit your event needs.
                </Alert>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12}>
                    {seatsEnabled === true && (
                        <Section>
                            <Header>Prevent Single-Seat Gaps</Header>

                            <Typography>
                                When customers choose their seats, this setting controls whether a single seat can be left empty between reservations.
                            </Typography>

                            <Content>
                                {orphanSeatOptions.map((option) => {
                                    const isSelected = orphanSeats.value === option.value;
                                    return (
                                        <OrphanSeatCard
                                            key={option.value}
                                            role="radio"
                                            aria-checked={isSelected}
                                            tabIndex={0}
                                            selected={isSelected ? 1 : 0}
                                            onClick={() => orphanSeats.setValue(option.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === ' ' || e.key === 'Enter') {
                                                    e.preventDefault();
                                                    orphanSeats.setValue(option.value);
                                                }
                                            }}
                                        >
                                            <Grid container>
                                                <Grid item xs={12} sm={option.image ? 8 : 12}>
                                                    <Box px={2} pt={2} pb={1}>
                                                        <Typography variant="h6">{option.label}</Typography>

                                                        <Typography variant="body2" color="text.secondary" mb={2}>
                                                            {option.description}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                {option.image ? (
                                                    <Grid item xs={12} sm={4}>
                                                        <Box height={'100%'} display="flex" justifyContent="center" alignItems={'center'}>
                                                            <OrphanSeatImage alt={option.imageAlt} src={option.image} />
                                                        </Box>
                                                    </Grid>
                                                ) : null}
                                            </Grid>

                                        </OrphanSeatCard>
                                    );
                                })}
                                {orphanSeats.error && <Typography color="error">{orphanSeats.error}</Typography>}
                            </Content>
                        </Section>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Section>
                        <Header>Add Fine Print to Checkout</Header>

                        <Typography>
                            Include any important details, such as terms and conditions or special
                            instructions, at the bottom of the checkout screen.
                        </Typography>

                        <Content>
                            <Box width="100%" mb={2}>
                                <FormControlLabel
                                    label="Add fine print"
                                    control={
                                        <Switch checked={fpEnabled} onChange={handleFpToggle} />
                                    }
                                />
                            </Box>

                            <SdTextArea
                                {...finePrint.formProps}
                                label="Fine Print"
                                placeholder="e.g. Please note: All sales are final. This is a rain-or-shine event. No refunds or exchanges."
                                minRows={6}
                                maxRows={6}
                            />
                        </Content>
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section>
                        <Header>Redirect Attendees After Checkout</Header>

                        <Typography>
                            Want to guide your attendees to a specific page after they complete
                            their purchase? Add a custom button to the confirmation screen.
                        </Typography>

                        <Content>
                            <Box width="100%" mb={2}>
                                <FormControlLabel
                                    label="Add button"
                                    control={
                                        <Switch checked={ctaEnabled} onChange={handleCtaToggle} />
                                    }
                                />
                            </Box>

                            <SdTextField
                                {...ctaDesc.formProps}
                                label="Short message to the attendee"
                                placeholder="e.g. Visit our website for more details"
                            />

                            <SdTextField
                                {...ctaBtnText.formProps}
                                label="Text that will appear on the button."
                                placeholder="e.g. Learn More"
                                inputProps={{ maxLength: 25 }}
                            />

                            <SdTextField
                                {...ctaBtnUrl.formProps}
                                label="Where will this button take them?"
                                placeholder="e.g. https://yourwebsite.com"
                            />
                        </Content>
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section>
                        <Header>Add a Custom Message to Ticket Emails</Header>

                        <Typography>
                            Include a personal note in the email that delivers tickets to your
                            attendees. Avoid content that may get the email flagged for spam. Your
                            customer may not receive the email if this happens. E.g. no references
                            to prizes, draws, or "winning." Keep it professional and event-focused.
                        </Typography>

                        <Content>
                            <Box width="100%" mb={2}>
                                <FormControlLabel
                                    label="Add message"
                                    control={
                                        <Switch
                                            checked={hostNoteEnabled}
                                            onChange={handleHostNoteToggle}
                                        />
                                    }
                                />
                            </Box>

                            <SdTextArea
                                {...hostNote.formProps}
                                label="Personalized Message"
                                placeholder="e.g. We're excited to have you join us. Stay tuned for event updates, and don't forget to check out our website for more information. See you soon."
                                minRows={6}
                                maxRows={6}
                            />
                        </Content>
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section>
                        <Header>Add Tracking to Your Event Page</Header>

                        <Typography>
                            Track your event's performance with a Meta Pixel or Google Analytics.
                            Enter your Meta Pixel ID or Google Analytics Tracking ID below. (More
                            tracking options coming soon!)
                        </Typography>

                        <Content>
                            <Box width="100%" mb={2}>
                                <FormControlLabel
                                    label="Add Meta Pixel"
                                    control={
                                        <Switch
                                            checked={metaPixelEnabled}
                                            onChange={handleMetaPixelToggle}
                                        />
                                    }
                                />
                            </Box>

                            <SdTextField
                                {...metaPixel.formProps}
                                label="Your Meta Pixel ID"
                                placeholder="Your 15-16 digit code"
                                inputProps={{ maxLength: 16 }}
                            />
                        </Content>
                        <Content>
                            <Box width="100%" mb={2}>
                                <FormControlLabel
                                    label="Add Google Analytics"
                                    control={
                                        <Switch
                                            checked={googleAnalyticsEnabled}
                                            onChange={handleGoogleAnalyticsToggle}
                                        />
                                    }
                                />
                            </Box>

                            <SdTextField
                                {...googleAnalytics.formProps}
                                label="Your Google Analytics Tracking ID"
                                placeholder="G-XXXXXXXXXX"
                                inputProps={{ maxLength: 12 }}
                            />
                        </Content>
                    </Section>
                </Grid>

                {defaultValues.robinEnabledFlag === true && (
                    <Grid item xs={12}>
                        <Section>
                            <Header>Add a Robin</Header>

                            <Typography>
                                Give customers the option to connect with your Robin agent. Those who
                                opt in will receive an introductory message from Robin over SMS.
                            </Typography>

                            <Content>
                                <Box width="100%" mb={2}>
                                    <FormControlLabel
                                        label="Add Robin"
                                        control={
                                            <Switch
                                                checked={robinEnabled}
                                                onChange={handleRobinToggle}
                                            />
                                        }
                                    />
                                </Box>

                                {robinEnabled && (
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        error={Boolean(robinError)}
                                        sx={{ mb: 3 }}
                                    >
                                        <InputLabel color='secondary' id="robin-agent-label">Robin</InputLabel>
                                        <Select
                                            labelId="robin-agent-label"
                                            label="Robin"
                                            value={hostWebhookId}
                                            onChange={handleRobinChange}
                                            renderValue={(selected) => {
                                                const agent = robinAgents.find(
                                                    (item) =>
                                                        String(item.host_webhook_id) === String(selected),
                                                );
                                                return agent ? formatRobinLabel(agent) : '';
                                            }}
                                            sx={{
                                                color:
                                                    selectedRobinAgent && !selectedRobinAgent.status === 1
                                                        ? 'text.disabled'
                                                        : 'text.primary',
                                            }}
                                        >
                                            {robinAgents.length === 0 && (
                                                <MenuItem value="" disabled>
                                                    No Robin agents connected — add one in Settings
                                                </MenuItem>
                                            )}
                                            {robinAgents.map((agent) => (
                                                <MenuItem
                                                    key={agent.host_webhook_id}
                                                    value={agent.host_webhook_id}
                                                    sx={{
                                                        color: agent.status === 1
                                                            ? 'text.primary'
                                                            : 'text.disabled',
                                                    }}
                                                >
                                                    {formatRobinLabel(agent)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {robinError && (
                                            <Typography variant="caption" color="error" mt={0.5}>
                                                {robinError}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}
                            </Content>
                        </Section>
                    </Grid>
                )}
            </Grid>

            <FormSubmitBar
                unsavedChanges={unsavedChanges}
                loading={updating}
                onCancel={handleReset}
                onSubmit={handleSubmit}
            />
        </FormContainer>
    );
}

const FormContainer = styled(Box)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(8),
    },
}));

const SdTextField = styled(TextField)({
    marginBottom: '24px',
    width: '100%',
});
SdTextField.defaultProps = { variant: 'outlined' };

const SdTextArea = styled(TextArea)({
    marginBottom: '24px',
    width: '100%',
});

const OrphanSeatCard = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
    margin: theme.spacing(1),
    border: `1px solid ${selected ? theme.palette.secondary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: selected ? `0 0 0 1px ${theme.palette.info.main}` : 'none',
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.info.main}`,
        outlineOffset: 2,
    },
}));

const OrphanSeatImage = styled('img')({
    maxWidth: '100%',
    maxHeight: 78,
});
